import * as $ from "jquery";
import { merge, noop } from "lodash";
import * as React from "react";
import { Component, DependencyList, useEffect } from "react";
import "../../../Content/Styles/EasyProjects/Default/ep-custom-tooltip.less";
import { QTip2 } from "../../EasyProjectsCommon/General";
import { understandChildren } from "../../utils/understandChildren";
import { QTooltipApi, QTooltipOptions } from "./types";

type ContentUpdatedCallback = () => void;
const TooltipContext = React.createContext<ContentUpdatedCallback>(noop);

export type QTooltipTargetProps = Pick<React.DOMAttributes<HTMLElement>, "onMouseEnter" | "onMouseMove" | "onFocus">;

const optionsWithShowOnFocus = {
    show: { ready: false, event: "mouseenter focus" },
    hide: { event: "mouseleave blur" },
};

export interface QTooltipProps {
    options?: QTooltipOptions;
    viewport?: HTMLDivElement;
    lazyInit?: boolean;
    showFocus?: boolean;
}

interface State {
    isTooltipVisible: boolean;
}

export class QTooltip extends Component<QTooltipProps, State> {
    private qTooltipRef: React.RefObject<HTMLDivElement>;
    private qTooltipContainRef: React.RefObject<HTMLDivElement>;
    private apiRef: React.MutableRefObject<QTooltipApi>;
    private options: QTooltipOptions;
    private isInitialized: boolean;

    constructor(props: QTooltipProps) {
        super(props);
        this.state = { isTooltipVisible: false };
        this.qTooltipRef = React.createRef();
        this.qTooltipContainRef = React.createRef();
        this.apiRef = React.createRef();

        ep.initTooltipSettings();
        const options = props.options ?? {};
        this.options = merge(
            {},
            ep.tooltipData,
            this.props.showFocus
                ? optionsWithShowOnFocus
                : {
                      show: { ready: false },
                  },
            {
                ...options,
                content: {
                    text: (_: any, api: QTooltipApi) => {
                        this.apiRef.current = api;
                        return this.qTooltipContainRef.current?.innerHTML;
                    },
                },
                events: {
                    ...options.events,
                    show: (...args: any[]) => {
                        options.events?.show?.(...args);
                        this.setState({ isTooltipVisible: true });
                    },
                    hide: (...args: any[]) => {
                        options.events?.hide?.(...args);
                        this.setState({ isTooltipVisible: false });
                    },
                },
            }
        );

        if (this.props.viewport) {
            this.options.position.viewport = $(this.props.viewport);
        }
    }

    componentDidMount() {
        if (!this.props.lazyInit) {
            this._initQtip();
        }
    }

    componentWillUnmount() {
        if (this.isInitialized) {
            QTip2(this.qTooltipRef.current, "destroy", true);
        }
    }

    _initQtip = () => {
        if (!this.isInitialized && this.qTooltipRef.current) {
            QTip2(this.qTooltipRef.current, this.options);
            this.isInitialized = true;
        }
    };

    onContentUpdated = () => {
        if (this.qTooltipContainRef.current && this.apiRef.current && !this.apiRef.current.destroyed) {
            this.apiRef.current.set("content.text", this.qTooltipContainRef.current.innerHTML);
        }
    };

    renderContent(tooltipContent: React.ReactElement<any>) {
        return (
            <div style={{ display: "none" }} ref={this.qTooltipContainRef}>
                {tooltipContent}
            </div>
        );
    }

    renderInnerContent(innerContent: React.ReactElement<any>) {
        let targetProps: QTooltipTargetProps = {};
        if (this.props.lazyInit) {
            targetProps = {
                onMouseEnter: this._initQtip,
                onMouseMove: this._initQtip,
                onFocus: this.props.showFocus ? this._initQtip : null,
            };
        }
        return React.cloneElement(innerContent, {
            ref: this.qTooltipRef,
            ...targetProps,
        });
    }

    render() {
        const { children } = this.props;
        const { target, content } = understandChildren(children);

        return (
            <>
                {this.renderInnerContent(target)}
                {this.state.isTooltipVisible && (
                    <TooltipContext.Provider value={this.onContentUpdated}>
                        {this.renderContent(content)}
                    </TooltipContext.Provider>
                )}
            </>
        );
    }
}

/**
 * Allows update tooltip content when any of dependencies is updated.
 */
export const useUpdateTooltip = (callback: (updateTooltip: ContentUpdatedCallback) => void, deps: DependencyList) => {
    const tooltipUpdated = React.useContext(TooltipContext);
    useEffect(() => {
        callback(tooltipUpdated);
    }, deps);
};
